import React, { useState } from 'react';
import { Plus, Search, Truck, CheckCircle, Clock, Trash2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { usePagination, useDebounce } from '../hooks/useCustom';
import Table from '../components/Table';
import Badge from '../components/Badge';
import Pagination from '../components/Pagination';
import Modal from '../components/Modal';
import { ConfirmModal } from '../components/Modal';
import { Input, Select, FormRow } from '../components/FormComponents';
import Breadcrumb from '../components/Breadcrumb';
import { formatDate, filterByMultipleFields } from '../utils/helpers';
import { hondaPrices } from '../utils/helpers';

const emptyForm = { namaPembeli: '', tipeMobil: '', tanggalKirim: '', noPolisi: '', statusPengiriman: 'Proses' };

export default function Delivery() {
  const { state, dispatch, showNotification } = useApp();
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const debouncedSearch = useDebounce(search, 300);
  const filtered = filterByMultipleFields(state.delivery, debouncedSearch, ['namaPembeli', 'tipeMobil', 'noPolisi']);
  const { currentPage, totalPages, currentData, goToPage } = usePagination(filtered, 10);

  const carOptions = Object.keys(hondaPrices).map(c => ({ value: c, label: c }));

  const openAdd = () => { setForm({ ...emptyForm, tanggalKirim: new Date().toISOString().split('T')[0] }); setEditData(null); setShowModal(true); };
  const openEdit = (d) => { setForm({ ...d }); setEditData(d); setShowModal(true); };

  const handleSubmit = () => {
    if (!form.namaPembeli || !form.tipeMobil || !form.tanggalKirim) return showNotification('Semua field wajib diisi', 'error');
    if (editData) {
      dispatch({ type: 'UPDATE_DELIVERY', payload: form });
      showNotification('Delivery berhasil diperbarui');
    } else {
      dispatch({ type: 'ADD_DELIVERY', payload: form });
      showNotification('Delivery berhasil ditambahkan');
    }
    setShowModal(false);
  };

  const columns = [
    { key: 'namaPembeli', label: 'Nama Pembeli', sortable: true },
    { key: 'tipeMobil', label: 'Tipe Mobil', sortable: true },
    { key: 'noPolisi', label: 'No. Polisi', render: v => v ? <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{v}</span> : '-' },
    { key: 'tanggalKirim', label: 'Tgl Kirim', render: v => formatDate(v) },
    { key: 'statusPengiriman', label: 'Status', render: v => <Badge status={v} /> },
    { key: 'actions', label: '', render: (_, row) => (
      <div className="flex gap-1">
        {row.statusPengiriman === 'Proses' && (
          <button
            onClick={() => { dispatch({ type: 'UPDATE_DELIVERY', payload: { ...row, statusPengiriman: 'Terkirim' } }); showNotification('Status delivery diperbarui'); }}
            className="btn-success py-1 px-2 text-xs"
          ><CheckCircle size={12}/>Selesai</button>
        )}
        <button onClick={() => openEdit(row)} className="btn-secondary py-1 px-2 text-xs">Edit</button>
        <button onClick={() => setDeleteId(row.id)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"><Trash2 size={14}/></button>
      </div>
    )},
  ];

  const inProcess = state.delivery.filter(d => d.statusPengiriman === 'Proses').length;
  const delivered = state.delivery.filter(d => d.statusPengiriman === 'Terkirim').length;

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: 'Delivery' }]} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Delivery Tracking</h1>
          <p className="text-sm text-gray-500">{state.delivery.length} unit total</p>
        </div>
        <button onClick={openAdd} className="btn-primary"><Plus size={16}/>Tambah Delivery</button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card flex items-center gap-4">
          <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center"><Truck size={20} className="text-orange-600" /></div>
          <div><p className="text-xs text-gray-500">Total Unit</p><p className="text-xl font-bold text-gray-700">{state.delivery.length}</p></div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center"><Clock size={20} className="text-yellow-600" /></div>
          <div><p className="text-xs text-gray-500">Dalam Proses</p><p className="text-xl font-bold text-yellow-600">{inProcess}</p></div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center"><CheckCircle size={20} className="text-green-600" /></div>
          <div><p className="text-xs text-gray-500">Terkirim</p><p className="text-xl font-bold text-green-600">{delivered}</p></div>
        </div>
      </div>

      {/* Search */}
      <div className="card">
        <div className="relative max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cari nama, tipe mobil, plat..." className="form-input pl-9" />
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <Table columns={columns} data={currentData} emptyMessage="Belum ada data delivery" />
        <div className="px-4 py-3 border-t border-gray-50">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={filtered.length} itemsPerPage={10} />
        </div>
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editData ? 'Edit Delivery' : 'Tambah Delivery'} size="md"
        footer={<><button onClick={() => setShowModal(false)} className="btn-secondary">Batal</button><button onClick={handleSubmit} className="btn-primary">Simpan</button></>}
      >
        <div className="space-y-4">
          <Input label="Nama Pembeli *" value={form.namaPembeli} onChange={e => setForm({...form, namaPembeli: e.target.value})} placeholder="Nama pembeli" />
          <Select label="Tipe Mobil *" value={form.tipeMobil} onChange={e => setForm({...form, tipeMobil: e.target.value})} options={carOptions} placeholder="Pilih tipe mobil" />
          <FormRow>
            <Input label="No. Polisi" value={form.noPolisi} onChange={e => setForm({...form, noPolisi: e.target.value})} placeholder="B 1234 ABC" />
            <Input label="Tanggal Kirim *" type="date" value={form.tanggalKirim} onChange={e => setForm({...form, tanggalKirim: e.target.value})} />
          </FormRow>
          <Select label="Status Pengiriman" value={form.statusPengiriman} onChange={e => setForm({...form, statusPengiriman: e.target.value})} options={[{value:'Proses',label:'Proses'},{value:'Terkirim',label:'Terkirim'}]} />
        </div>
      </Modal>

      <ConfirmModal isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={() => { dispatch({ type: 'DELETE_DELIVERY', payload: deleteId }); showNotification('Delivery dihapus', 'error'); setDeleteId(null); }} title="Hapus Delivery" message="Yakin ingin menghapus data delivery ini?" confirmLabel="Hapus" confirmClass="btn-danger" />
    </div>
  );
}
